import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { featuredComics } from '@/mocks/comics';

export default function ComicDetail() {
  const { slug } = useParams<{ slug: string }>();

  const comic = useMemo(() => {
    return featuredComics.find((c) => c.id === slug) || null;
  }, [slug]);

  const chapters = useMemo(() => {
    if (!comic) return [];
    return Array.from({ length: comic.chapters }, (_, i) => ({
      num: comic.chapters - i,
      title: `第${comic.chapters - i}话`,
      date: `2026-${String(6 - Math.floor(i / 20)).padStart(2, '0')}-${String(30 - (i % 28)).padStart(2, '0')}`,
    }));
  }, [comic]);

  // SEO article structured data
  const articleLD = comic ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 古风漫画网`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: '2024-01-15',
    dateModified: comic.updateDate,
    image: comic.cover,
    publisher: {
      '@type': 'Organization',
      name: '古风漫画网',
      logo: { '@type': 'ImageObject', url: 'http://xudongprc.com/logo.png' },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `http://xudongprc.com/comic/${comic.id}`,
    },
  } : null;

  const breadcrumbLD = comic ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://xudongprc.com/' },
      { '@type': 'ListItem', position: 2, name: '全部分类', item: 'http://xudongprc.com/category/all' },
      { '@type': 'ListItem', position: 3, name: comic.category, item: `http://xudongprc.com/category/${comic.categorySlug}` },
      { '@type': 'ListItem', position: 4, name: comic.title },
    ],
  } : null;

  // Not found state
  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-20 md:pt-24 min-h-screen bg-background-50 flex items-center justify-center">
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto bg-background-100 rounded-3xl flex items-center justify-center mb-6">
              <i className="ri-book-2-line text-foreground-300 text-3xl"></i>
            </div>
            <h1 className="text-2xl font-bold text-foreground-950 font-heading mb-3">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-8">抱歉，您要查看的漫画不存在或已被下架</p>
            <Link
              to="/category/all"
              className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 text-background-50 rounded-full text-sm font-medium hover:bg-accent-600 transition-all whitespace-nowrap cursor-pointer"
            >
              浏览全部作品
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="pt-20 md:pt-24 min-h-screen bg-background-50">
        {/* Structured data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLD) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLD) }}
        />

        <div className="px-4 md:px-6 max-w-7xl mx-auto py-10">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to="/category/all" className="hover:text-primary-500 transition-colors">全部分类</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to={`/category/${comic.categorySlug}`} className="hover:text-primary-500 transition-colors">
              {comic.category}
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-800 font-medium">{comic.title}</span>
          </nav>

          {/* Comic info */}
          <article className="flex flex-col md:flex-row gap-8 mb-12">
            <div className="w-full md:w-[280px] shrink-0">
              <div className="aspect-[3/4] rounded-2xl overflow-hidden bg-background-100">
                <img
                  src={comic.cover}
                  alt={comic.title}
                  title={comic.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <Link
                  to={`/category/${comic.categorySlug}`}
                  className="px-2.5 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full hover:bg-accent-200 transition-colors cursor-pointer"
                >
                  {comic.category}
                </Link>
                <span className="px-2.5 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                  {comic.status}
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 font-heading">
                {comic.title}
              </h1>
              <p className="text-foreground-500 mt-1">作者：<strong>{comic.author}</strong></p>
              <p className="text-foreground-600 mt-4 leading-relaxed max-w-xl">{comic.description}</p>
              <div className="flex items-center gap-6 mt-6 text-sm flex-wrap">
                <div>
                  <span className="text-foreground-400">评分</span>
                  <p className="text-xl font-bold text-accent-500">{comic.rating}</p>
                </div>
                <div>
                  <span className="text-foreground-400">阅读量</span>
                  <p className="text-lg font-semibold text-foreground-900">{comic.views}</p>
                </div>
                <div>
                  <span className="text-foreground-400">总章节</span>
                  <p className="text-lg font-semibold text-foreground-900">{comic.chapters}话</p>
                </div>
                <div>
                  <span className="text-foreground-400">更新日期</span>
                  <p className="text-lg font-semibold text-foreground-900">{comic.updateDate}</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <a
                  href={`/comic/${comic.id}/read/1`}
                  className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-accent-500 text-background-50 rounded-full text-base font-medium hover:bg-accent-600 transition-all whitespace-nowrap cursor-pointer"
                >
                  开始阅读
                  <i className="ri-play-line"></i>
                </a>
                <button className="inline-flex items-center justify-center gap-2 px-8 py-3 border border-background-300 text-foreground-700 rounded-full text-base font-medium hover:bg-background-100 transition-all whitespace-nowrap cursor-pointer">
                  <i className="ri-bookmark-line"></i>
                  加入收藏
                </button>
              </div>
            </div>
          </article>

          {/* Chapter list */}
          <section className="bg-background-100 rounded-2xl p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-foreground-950 font-heading">
                章节列表
              </h2>
              <div className="flex items-center gap-3">
                <button className="px-3 py-1.5 bg-background-50 text-foreground-600 text-xs rounded-full hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                  正序
                </button>
                <button className="px-3 py-1.5 bg-foreground-900 text-background-50 text-xs rounded-full cursor-pointer whitespace-nowrap">
                  倒序
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {chapters.map((ch) => (
                <a
                  key={ch.num}
                  href={`/comic/${comic.id}/read/${ch.num}`}
                  className="flex items-center justify-between px-4 py-3 bg-background-50 rounded-xl hover:bg-primary-50 transition-colors cursor-pointer group"
                >
                  <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors">
                    {ch.title}
                  </span>
                  <span className="text-xs text-foreground-400">{ch.date}</span>
                </a>
              ))}
            </div>
          </section>

          {/* Related comics */}
          <section className="mt-14">
            <h2 className="text-xl font-bold text-foreground-950 font-heading mb-6">
              同类<span className="text-accent-500">推荐</span>
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {featuredComics
                .filter((c) => c.categorySlug === comic.categorySlug && c.id !== comic.id)
                .slice(0, 6)
                .map((related) => (
                  <Link
                    key={related.id}
                    to={`/comic/${related.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-100 mb-3">
                      <img
                        src={related.cover}
                        alt={related.title}
                        title={related.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                      {related.title}
                    </h3>
                    <div className="flex items-center gap-1 mt-1">
                      <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                      <span className="text-xs text-foreground-500">{related.rating}</span>
                    </div>
                  </Link>
                ))}
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}